import { Css, IconButton, Palette, useTestIds } from "@homebound/beam";
// @ts-ignore `?url` is a Vite import suffix, and `@types/react-pdf` is still on v5.
import pdfWorker from "pdfjs-dist/build/pdf.worker.js?url";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
// Makes the text layer's spans transparent and lays them over the canvas.
import "react-pdf/dist/esm/Page/TextLayer.css";
import { ListRange, Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { Loading } from "src/components/Loading";
import { openDownload, openNewTab } from "src/utils/window";

// A module-scope side effect, so set it here rather than rely on `PdfViewer` having been imported first.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

export type AiSourcePdfViewerApi = { scrollToPage: (page: number) => void };

type AiSourcePdfViewerProps = {
  asset: { id: string; fileName?: string | null; downloadUrl: string; attachmentUrl: string };
  currentPage: number;
  onPageChange: (page: number) => void;
};

export const AiSourcePdfViewer = forwardRef<AiSourcePdfViewerApi, AiSourcePdfViewerProps>(
  function AiSourcePdfViewer(props, ref) {
    const { asset, currentPage, onPageChange } = props;
    const tid = useTestIds(props, "aiSourcePdfViewer");
    // Read at load, before the list mounts, because the sheet shape sizes the loading placeholders below.
    const [doc, setDoc] = useState<{ numPages: number; aspect: number }>();
    const numPages = doc?.numPages ?? 0;
    const [scale, setScale] = useState(1);
    const pageWidth = Math.round(basePageWidth * scale);
    const [railOpen, setRailOpen] = useState(true);
    const pagesRef = useRef<VirtuosoHandle>(null);
    const railRef = useRef<VirtuosoHandle>(null);
    // The last sheets can't scroll to the top, so after a jump the top of the visible range is the page
    // above the one asked for. Remember the jump and keep it current while it's on screen.
    const jumpedTo = useRef<number | undefined>(currentPage);

    const scrollToPage = useCallback(
      (page: number) => {
        jumpedTo.current = page;
        pagesRef.current?.scrollToIndex({ index: clampPage(page, numPages) - 1, align: "start" });
      },
      [numPages],
    );

    useImperativeHandle(ref, () => ({ scrollToPage }), [scrollToPage]);

    // Keep the current sheet's thumbnail on screen as the document moves. Scrolls the rail only if needed.
    useEffect(() => {
      railRef.current?.scrollIntoView({ index: clampPage(currentPage, numPages) - 1 });
    }, [currentPage, numPages]);

    function jumpTo(page: number) {
      onPageChange(page);
      scrollToPage(page);
    }

    function onRangeChanged({ startIndex, endIndex }: ListRange) {
      const jumped = jumpedTo.current;
      if (jumped && jumped - 1 >= startIndex && jumped - 1 <= endIndex) return onPageChange(jumped);
      jumpedTo.current = undefined;
      onPageChange(startIndex + 1);
    }

    function zoom(by: number) {
      setScale((s) => Math.min(maxScale, Math.max(minScale, Math.round((s + by) * 100) / 100)));
    }

    return (
      <div css={Css.df.fdc.fg1.mh0.bgGray200.$} {...tid}>
        <div css={Css.df.aic.gap2.fs0.hPx(48).px2.bgGray900.white.$}>
          <IconButton
            icon={railOpen ? "chevronLeft" : "menu"}
            color={Palette.White}
            tooltip={railOpen ? "Hide thumbnails" : "Show thumbnails"}
            onClick={() => setRailOpen((open) => !open)}
            {...tid.toggleRail}
          />
          <span css={Css.xs.truncate.mra.$} {...tid.fileName}>
            {asset.fileName ?? "Construction document"}
          </span>
          <span css={Css.xsSb.$} {...tid.pageCount}>
            {clampPage(currentPage, numPages)} / {numPages || "—"}
          </span>
          <IconButton
            icon="minus"
            color={Palette.White}
            tooltip="Zoom out"
            onClick={() => zoom(-zoomStep)}
            {...tid.zoomOut}
          />
          <span css={Css.xsSb.wPx(48).tac.$} {...tid.zoom}>
            {Math.round(scale * 100)}%
          </span>
          <IconButton
            icon="plus"
            color={Palette.White}
            tooltip="Zoom in"
            onClick={() => zoom(zoomStep)}
            {...tid.zoomIn}
          />
          <IconButton
            icon="download"
            color={Palette.White}
            tooltip="Download"
            onClick={() => openDownload(asset.attachmentUrl)}
            {...tid.download}
          />
          {/* Canvases print badly, so hand the raw PDF to the browser's own viewer instead. */}
          <IconButton
            icon="linkExternal"
            color={Palette.White}
            tooltip="Open in a new tab to print"
            onClick={() => openNewTab(asset.downloadUrl)}
            {...tid.print}
          />
        </div>

        {/* react-pdf's wrapper is a plain block, so it joins the flex column via `css` (it forwards
            `className`) or the list below collapses to zero height. */}
        <Document
          css={Css.df.fdc.fg1.mh0.$}
          file={asset.downloadUrl}
          loading={<Loading />}
          error={
            <div css={Css.df.aic.jcc.p4.gray700.$} {...tid.loadError}>
              We couldn't load this document. Refresh the page to try again.
            </div>
          }
          onLoadSuccess={async (pdf) => {
            const { width, height } = (await pdf.getPage(1)).getViewport({ scale: 1 });
            setDoc({ numPages: pdf.numPages, aspect: height / width });
          }}
        >
          {doc && (
            <div css={Css.df.fg1.mh0.$}>
              {/* Unmounted when closed, so reopening re-centres on the current sheet. */}
              {railOpen && (
                <Virtuoso
                  ref={railRef}
                  style={{ height: "100%", width: railWidth }}
                  css={Css.fs0.bgGray800.$}
                  totalCount={numPages}
                  initialTopMostItemIndex={{ index: clampPage(currentPage, numPages) - 1, align: "center" }}
                  itemContent={(index) => (
                    <Thumbnail
                      page={index + 1}
                      isCurrent={index + 1 === clampPage(currentPage, numPages)}
                      placeholderHeight={Math.round(thumbnailWidth * doc.aspect)}
                      onClick={jumpTo}
                    />
                  )}
                  {...tid.rail}
                />
              )}
              <Virtuoso
                ref={pagesRef}
                style={{ height: "100%" }}
                css={Css.fg1.$}
                totalCount={numPages}
                initialTopMostItemIndex={clampPage(currentPage, numPages) - 1}
                rangeChanged={onRangeChanged}
                itemContent={(index) => (
                  <div css={Css.df.jcc.py1.$}>
                    <Page
                      pageNumber={index + 1}
                      width={pageWidth}
                      // Text is selectable, but browser find only reaches the pages mounted here. Revisit with a
                      // search UI over pdf.js's text content if reviewers need document-wide find.
                      renderTextLayer
                      renderAnnotationLayer={false}
                      // Sized like the real sheet: an empty loading page measures as just its padding, which shrinks
                      // the list and clamps any scroll near the end, so jumps to the last pages fall short.
                      loading={<div css={Css.wPx(pageWidth).hPx(Math.round(pageWidth * doc.aspect)).bgWhite.$} />}
                    />
                  </div>
                )}
                {...tid.pages}
              />
            </div>
          )}
        </Document>
      </div>
    );
  },
);

/** Page width at 100% zoom. */
const basePageWidth = 900;
const railWidth = 224;
const thumbnailWidth = 160;
const zoomStep = 0.25;
const minScale = 0.5;
const maxScale = 3;

/** `page` bounded to the document; a `?page=` can name one past the end. */
function clampPage(page: number, numPages: number): number {
  return Math.max(1, numPages ? Math.min(page, numPages) : page);
}

type ThumbnailProps = { page: number; isCurrent: boolean; placeholderHeight: number; onClick: (page: number) => void };

function Thumbnail(props: ThumbnailProps) {
  const { page, isCurrent, placeholderHeight, onClick } = props;
  const tid = useTestIds(props, "thumbnail");
  return (
    <button
      type="button"
      aria-label={`Page ${page}`}
      aria-current={isCurrent ? "page" : undefined}
      onClick={() => onClick(page)}
      css={Css.df.fdc.aic.gap1.w100.py1.$}
      {...tid}
    >
      {/* Rings the current sheet, as Chrome's rail does. An outline, so it doesn't shift the rail. */}
      <div css={Css.br4.if(isCurrent).add("outline", `4px solid ${Palette.Blue500}`).$}>
        <Page
          pageNumber={page}
          width={thumbnailWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          // Sized for the same reason as the main list's placeholders: an empty page would clamp the rail's scroll.
          loading={<div css={Css.wPx(thumbnailWidth).hPx(placeholderHeight).bgWhite.$} />}
        />
      </div>
      <span css={Css.xs.white.$}>{page}</span>
    </button>
  );
}
